import { Controller } from 'react-hook-form';
import { validate } from 'uuid';
import {
  InputSize,
  InputText,
  Select,
  SelectSize,
  SelectType,
  Textarea,
} from '@ui/src';
import AdditionalInfoBlock from '../../../individual/aml/additional-info-block';
import { BusinessActivitesTypes } from './types';
import { CustomerStatus } from '../../../../hoc/customers/types';
import RegulatedCompanyRow from './row';

import cn from './styles.module.css';

const BusinessActivities = ({
  control,
  clearErrors,
  setValue,
  status,
  errors,
  fields,
  businessActivities,
  customerMainClients,
  salesChannels,
  companyLegalName,
  customerSourceOfFunds,
}: BusinessActivitesTypes) => (
  <AdditionalInfoBlock title="Customer Business Activities">
    <>
      <RegulatedCompanyRow title="Does the Customer apply KYC procedures?">
        <Controller
          control={control}
          name="customerAppliesKycProcedures"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              placeholder="Select an option..."
              list={[
                {
                  name: 'Yes',
                  value: 'yes',
                },
                {
                  name: 'No',
                  value: 'no',
                },
              ]}
              value={value as string}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange(e);
                clearErrors(['customerAppliesKycProcedures']);
              }}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              error={errors?.customerAppliesKycProcedures?.message}
              fullWidth
              borderless
            />
          )}
        />
      </RegulatedCompanyRow>
      <RegulatedCompanyRow title="Which of the following activities does the Customer carry out? (select all that apply)">
        <Controller
          control={control}
          name="businessActivitiesIds"
          defaultValue={[]}
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              placeholder="Select an option..."
              list={businessActivities?.map((item) => ({
                name: item.value,
                value: item.id,
              }))}
              value={value}
              size={SelectSize.SMALL}
              onChange={(e) => {
                clearErrors(['businessActivitiesIds', 'hasBusinessActivity']);

                let nextValues: string[] = e.target.value as string[];

                if (
                  (e.target.value as string[])[
                    (e.target.value as string[]).length - 1
                  ] === 'NONE'
                ) {
                  nextValues = ['NONE'];
                  setValue('hasBusinessActivity', false);
                } else if (
                  (e.target.value as string[])?.find((item) => validate(item))
                ) {
                  if (
                    (e.target.value as string[])?.find(
                      (item) => item === 'NONE',
                    )
                  ) {
                    nextValues = (e.target.value as string[])?.filter(
                      (item) => item !== 'NONE',
                    );
                  }

                  if (!fields?.hasBusinessActivity) {
                    setValue('hasBusinessActivity', true);
                  }
                }

                onChange(nextValues);
              }}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              error={errors?.businessActivitiesIds?.message}
              mode={SelectType.MULTIPLE}
              fullWidth
              borderless
            />
          )}
        />
      </RegulatedCompanyRow>
      <RegulatedCompanyRow title="Customer's main clients">
        <Controller
          control={control}
          name="customersMainClientsIds"
          defaultValue={[]}
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              placeholder="Select an option..."
              list={
                customerMainClients?.map((item) => ({
                  name: item.value,
                  value: item.id,
                })) || []
              }
              value={value}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange(e);
                clearErrors(['customersMainClientsIds']);

                if (
                  !(e.target.value as string[])?.includes('Other') &&
                  fields?.customersMainClientsOther
                ) {
                  clearErrors(['customersMainClientsOther']);
                  setValue('customersMainClientsOther', '');
                }
              }}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              error={errors?.customersMainClientsIds?.message}
              mode={SelectType.MULTIPLE}
              fullWidth
              borderless
            />
          )}
        />
      </RegulatedCompanyRow>
      {fields?.customersMainClientsIds?.find((item) =>
        customerMainClients
          ?.find((client) => item === client.id)
          ?.value?.includes('Other'),
      ) && (
        <RegulatedCompanyRow title="Provide details">
          <Controller
            control={control}
            name="customersMainClientsOther"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <Textarea
                name={name}
                title=""
                placeholder="Enter details..."
                value={value}
                onChange={(e) => {
                  onChange(e);
                  clearErrors(['customersMainClientsOther']);
                }}
                className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                error={errors?.customersMainClientsOther?.message}
                disabled={status === CustomerStatus.CLOSED}
                tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              />
            )}
          />
        </RegulatedCompanyRow>
      )}
      <RegulatedCompanyRow title="How does the Customer sell products and/or services?">
        <Controller
          control={control}
          name="salesChannelsIds"
          defaultValue={[]}
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              placeholder="Select an option..."
              list={
                salesChannels?.map((item) => ({
                  name: item.value,
                  value: item.id,
                })) || []
              }
              value={value}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange(e);
                clearErrors(['salesChannelsIds']);

                if (
                  !(e.target.value as string[])?.includes('Other') &&
                  fields?.salesChannelsOther
                ) {
                  clearErrors(['salesChannelsOther']);
                  setValue('salesChannelsOther', '');
                }
              }}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              error={errors?.salesChannelsIds?.message}
              mode={SelectType.MULTIPLE}
              fullWidth
              borderless
            />
          )}
        />
      </RegulatedCompanyRow>
      {fields?.salesChannelsIds?.find((item) =>
        salesChannels
          ?.find((saleChannel) => item === saleChannel.id)
          ?.value?.includes('Other'),
      ) && (
        <RegulatedCompanyRow title="Provide details">
          <Controller
            control={control}
            name="salesChannelsOther"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <Textarea
                name={name}
                title=""
                placeholder="Enter details..."
                value={value}
                onChange={(e) => {
                  onChange(e);
                  clearErrors(['salesChannelsOther']);
                }}
                className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                error={errors?.salesChannelsOther?.message}
                disabled={status === CustomerStatus.CLOSED}
                tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              />
            )}
          />
        </RegulatedCompanyRow>
      )}
      <RegulatedCompanyRow title="Number of employees">
        <Controller
          control={control}
          name="numberOfEmployees"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <InputText
              name={name}
              placeholder="Enter number..."
              value={value}
              size={InputSize.SMALL}
              onChange={(e) => {
                onChange(e);
                clearErrors(['numberOfEmployees']);
              }}
              className="borderless"
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              error={errors?.numberOfEmployees?.message}
            />
          )}
        />
      </RegulatedCompanyRow>
      <RegulatedCompanyRow
        title={`Source of funds to be credited to the account in ${companyLegalName}`}
      >
        <Controller
          control={control}
          name="sourcesOfFundsIds"
          defaultValue={[]}
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              placeholder="Select an option..."
              list={
                customerSourceOfFunds?.map((item) => ({
                  name: item.value,
                  value: item.id,
                })) || []
              }
              value={value}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange(e);
                clearErrors(['sourcesOfFundsIds']);

                if (
                  !(e.target.value as string[])?.includes('Other') &&
                  fields?.sourcesOfFundsOther
                ) {
                  clearErrors(['sourcesOfFundsOther']);
                  setValue('sourcesOfFundsOther', '');
                }

                if (
                  !(e.target.value as string[])?.includes('Other') &&
                  fields?.sourcesOfFundsLoanCreditInstitution
                ) {
                  clearErrors(['sourcesOfFundsLoanCreditInstitution']);
                  setValue('sourcesOfFundsLoanCreditInstitution', '');
                }

                if (
                  !(e.target.value as string[])?.includes('Other') &&
                  fields?.sourcesOfFundsLoanBusinessPartner
                ) {
                  clearErrors(['sourcesOfFundsLoanBusinessPartner']);
                  setValue('sourcesOfFundsLoanBusinessPartner', '');
                }
              }}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              error={errors?.sourcesOfFundsIds?.message}
              mode={SelectType.MULTIPLE}
              fullWidth
              borderless
            />
          )}
        />
      </RegulatedCompanyRow>
      {fields?.sourcesOfFundsIds?.find((item) =>
        customerSourceOfFunds
          ?.find((client) => item === client.id)
          ?.value?.includes('Other'),
      ) && (
        <RegulatedCompanyRow title="Provide details">
          <Controller
            control={control}
            name="sourcesOfFundsOther"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <Textarea
                name={name}
                title=""
                placeholder="Enter details..."
                value={value}
                onChange={(e) => {
                  onChange(e);
                  clearErrors(['sourcesOfFundsOther']);
                }}
                className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                error={errors?.sourcesOfFundsOther?.message}
                disabled={status === CustomerStatus.CLOSED}
                tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              />
            )}
          />
        </RegulatedCompanyRow>
      )}
      {fields?.sourcesOfFundsIds?.find((item) =>
        customerSourceOfFunds
          ?.find((client) => item === client.id)
          ?.value?.includes('Loan from a credit institution'),
      ) && (
        <RegulatedCompanyRow title="Credit institution which have provided loan">
          <Controller
            control={control}
            name="sourcesOfFundsLoanCreditInstitution"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <Textarea
                name={name}
                placeholder="Enter details..."
                value={value}
                onChange={(e) => {
                  onChange(e);
                  clearErrors(['sourcesOfFundsLoanCreditInstitution']);
                }}
                className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                disabled={status === CustomerStatus.CLOSED}
                tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                error={errors?.sourcesOfFundsLoanCreditInstitution?.message}
              />
            )}
          />
        </RegulatedCompanyRow>
      )}
      {fields?.sourcesOfFundsIds?.find((item) =>
        customerSourceOfFunds
          ?.find((client) => item === client.id)
          ?.value?.includes('Loan from a business partner'),
      ) && (
        <RegulatedCompanyRow title="Business partner which provided loan">
          <Controller
            control={control}
            name="sourcesOfFundsLoanBusinessPartner"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <Textarea
                name={name}
                placeholder="Enter details..."
                value={value}
                onChange={(e) => {
                  onChange(e);
                  clearErrors(['sourcesOfFundsLoanBusinessPartner']);
                }}
                className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                disabled={status === CustomerStatus.CLOSED}
                tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                error={errors?.sourcesOfFundsLoanCreditInstitution?.message}
              />
            )}
          />
        </RegulatedCompanyRow>
      )}
      <RegulatedCompanyRow title="Does the Customer have accounts in other financial institutions?">
        <Controller
          control={control}
          name="hasAccountsInOtherFinancialInstitutions"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              placeholder="Select an option..."
              list={[
                {
                  name: 'Yes',
                  value: 'yes',
                },
                {
                  name: 'No',
                  value: 'no',
                },
              ]}
              value={value as string}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange(e);
                clearErrors(['hasAccountsInOtherFinancialInstitutions']);

                if (
                  e.target.value === 'no' &&
                  fields?.otherFinancialInstitutions
                ) {
                  clearErrors(['otherFinancialInstitutions']);
                  setValue('otherFinancialInstitutions', '');
                }
              }}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              error={errors?.hasAccountsInOtherFinancialInstitutions?.message}
              fullWidth
              borderless
            />
          )}
        />
      </RegulatedCompanyRow>
      {fields?.hasAccountsInOtherFinancialInstitutions === 'yes' && (
        <RegulatedCompanyRow title="Financial institutions at which customer has accounts">
          <Controller
            control={control}
            name="otherFinancialInstitutions"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <Textarea
                name={name}
                title=""
                placeholder="Enter details..."
                value={value}
                onChange={(e) => {
                  onChange(e);
                  clearErrors(['otherFinancialInstitutions']);
                }}
                className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                error={errors?.otherFinancialInstitutions?.message}
                disabled={status === CustomerStatus.CLOSED}
                tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              />
            )}
          />
        </RegulatedCompanyRow>
      )}
    </>
  </AdditionalInfoBlock>
);

export default BusinessActivities;
